import { Box, CircularProgress, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  EditCustomDataFields,
  ReadCustomDataFields,
} from '~/legacy-components/CustomDataFields';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';
import { LegacyHeadingWithActions } from '~/legacy-components/LegacyLayout';

import { useCustomData } from './useCustomData';
import { useSxStyles } from './useStyles';

export const CustomDataTab = () => {
  const { t } = useTranslation();
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const {
    isLoading,
    readFieldData,
    editFieldData,
    control,
    handleFormSubmit,
    canEdit,
    reset,
    editMode,
    setEditMode,
  } = useCustomData({
    employeeId: selectedEmployeeId,
  });

  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const isManager = hasRoleAccessOverEmployee([Roles.MANAGER]);
  const isHrAdmin = hasRoleAccessOverEmployee([Roles.HR_ADMIN]);

  const evaluateDisableField = (editableByEmp: boolean): boolean => {
    if (isHrAdmin) {
      return false;
    } else if (isManager) {
      return editableByEmp;
    } else {
      return !editableByEmp;
    }
  };

  const { contentPadding, spinner } = useSxStyles();

  return (
    <Box sx={contentPadding}>
      <LegacyHeadingWithActions heading={t('laCustomData')}>
        {editMode ? (
          <>
            <LegacyMuiButton
              onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                await handleFormSubmit();
              }}
              startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Save} />}
            >
              {t('laSave')}
            </LegacyMuiButton>

            <LegacyMuiButton
              onClick={() => {
                setEditMode(false);
                reset();
              }}
              startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
            >
              {t('laCancel')}
            </LegacyMuiButton>
          </>
        ) : (
          <>
            {canEdit && editFieldData.length > 0 && (
              <LegacyMuiButton
                onClick={() => setEditMode(true)}
                startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Edit} />}
              >
                {t('laEdit')}
              </LegacyMuiButton>
            )}
          </>
        )}
      </LegacyHeadingWithActions>
      {isLoading ? (
        <Box sx={spinner}>
          <CircularProgress size={25} />
        </Box>
      ) : (
        <form>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={1} alignItems="center">
              {editMode
                ? editFieldData.length
                  ? editFieldData.map((f, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <EditCustomDataFields
                          control={control}
                          key={`${index}/${f.name}`}
                          {...{
                            ...f,
                            inputProps: {
                              ...f.inputProps,
                              disabled: evaluateDisableField(!!f.editableByEmp),
                            },
                          }}
                        />
                      </Grid>
                    ))
                  : null
                : readFieldData.length
                ? readFieldData.map((f, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <ReadCustomDataFields key={`${index}/${f.name}`} {...f} />
                    </Grid>
                  ))
                : null}
            </Grid>
          </Box>
        </form>
      )}
    </Box>
  );
};
